import React, { useState, useEffect, useRef } from 'react';
import './PostsTab.css';
import '../../../styles/social-posts.css';
import { FaPaperPlane, FaImage, FaLink, FaTimes, FaEdit, FaSave, FaVideo, FaMusic, FaRetweet, FaRegCommentDots, FaShareAlt, FaTrash, FaPaperclip, FaExternalLinkAlt, FaEllipsisV } from 'react-icons/fa';
import { getEmbedSrc, extractEmbedFromContent } from '../../../utils/embed';
import Comments from '../../Home/Components/Comments';
import '../../Home/HomeModal.css';
import { fetchReactions } from '../../../api/reactions';
import { fetchRepostCount, addRepost, fetchShareCount, addShare } from '../../../api/postActions';
import Modal from 'react-modal';
import { useAuth } from '../../../contexts/authContext';
import PostReactions from '../../Home/Components/PostReactions';
import supabase from '../../../supabaseClient';
import DockedComposer from '../../../components/DockedComposer';
import { ProfileLinguaSnaps } from '../../../components/LinguaSnaps';

const PostsTab = ({
  posts,
  newPost,
  setNewPost,
  postMedia,
  setPostMedia,
  postMediaPreview,
  setPostMediaPreview,
  localEmbedPreview,
  setLocalEmbedPreview,
  fileInputRef,
  editingPostId,
  setEditingPostId,
  editedPostContent,
  setEditedPostContent,
  handlePostSubmit,
  handleMediaSelect,
  handleRemoveMedia,
  handleSaveEditPost,
  handleCancelEditPost,
  handleDeletePost,
  profile,
  isSubmittingPost,
  isLoading = false,
  isOwnProfile = false
}) => {
  const [embedMenuOpen, setEmbedMenuOpen] = useState({});
  const embedMenuRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (embedMenuRef.current && !embedMenuRef.current.contains(e.target)) {
        setEmbedMenuOpen({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEmbedMenu = (itemId) => {
    setEmbedMenuOpen(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getEmbedPlatform = (url) => {
    if (/youtube|youtu\.be/i.test(url)) return 'YouTube';
    if (/soundcloud/i.test(url)) return 'SoundCloud';
    return 'Link';
  };

  const copyEmbedLink = (url) => {
    navigator.clipboard?.writeText(url);
    setEmbedMenuOpen({});
  };

  const viewOnPlatform = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setEmbedMenuOpen({});
  };
  const { user: authUser } = useAuth();
  const [reactionsMap, setReactionsMap] = useState({});
  const [repostCounts, setRepostCounts] = useState({});
  const [shareCounts, setShareCounts] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const userId = profile?.id;
  const [ownerMenuOpen, setOwnerMenuOpen] = useState({});

  // Load reposts, shares, and comments counts for posts (reactions come from server)
  useEffect(() => {
    async function loadAll() {
      const newReactions = {};
      const newReposts = {};
      const newShares = {};
      const newCounts = {};
      for (const post of posts) {
        // Use server-provided reactions if available
        newReactions[post.id] = post.reactionsByType || {};
        // Reposts
        newReposts[post.id] = await fetchRepostCount(post.id);
        // Shares
        newShares[post.id] = await fetchShareCount(post.id);
        // Comments count
        try {
          const { count } = await supabase
            .from('comments')
            .select('*', { count: 'exact', head: true })
            .eq('post_id', post.id);
          newCounts[post.id] = count || 0;
        } catch (_) {
          newCounts[post.id] = 0;
        }
      }
      setReactionsMap(newReactions);
      setRepostCounts(newReposts);
      setShareCounts(newShares);
      setCommentCounts(newCounts);
    }
    if (posts && posts.length > 0) loadAll();
  }, [posts]);

  // Unified reaction picker via shared component
  const getAuthHeaders = async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const handleReactionUpdate = async (postId, prevType, nextType) => {
    // Optimistically update local counts based on delta
    setReactionsMap(prev => {
      const curr = { ...(prev[postId] || {}) };
      if (prevType) curr[prevType] = Math.max(0, (curr[prevType] || 0) - 1);
      if (nextType) curr[nextType] = (curr[nextType] || 0) + 1;
      return { ...prev, [postId]: curr };
    });
    // Optionally refresh from server to be exact
    try {
      const reactions = await fetchReactions(postId);
      setReactionsMap(prev => ({
        ...prev,
        [postId]: reactions.reduce((acc, r) => {
          acc[r.reaction_type] = (acc[r.reaction_type] || 0) + 1;
          return acc;
        }, {})
      }));
    } catch {}
  };

  // Handle repost
  const handleRepost = async (post) => {
    try {
      await addRepost(post.id, userId);
      const count = await fetchRepostCount(post.id);
      setRepostCounts(prev => ({ ...prev, [post.id]: count }));
      alert('Reposted!');
    } catch (err) {
      alert('Failed to repost: ' + err.message);
    }
  };

  // Handler for DockedComposer submission (bypasses parent state, posts directly)
  const handleDockedSubmit = async (content, mediaFile, mediaType, embedPreview) => {
    const { content: cleanContent, embedUrl } = extractEmbedFromContent(content);
    const embed_url = embedUrl || null;
    if (!cleanContent && !mediaFile && !embed_url) return;
    
    try {
      let uploadedUrl = null;
      let uploadedType = null;
      if (mediaFile) {
        const ext = mediaFile.name.split('.').pop();
        const filePath = `posts/${profile.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
        let { error: uploadError } = await supabase.storage
          .from('post-media')
          .upload(filePath, mediaFile, { cacheControl: '3600', upsert: false });
        if (uploadError) {
          alert('Failed to upload media');
          throw new Error('Upload failed');
        }
        const { data: publicUrlData } = supabase
          .storage
          .from('post-media')
          .getPublicUrl(filePath);
        uploadedUrl = publicUrlData.publicUrl;
        uploadedType = mediaFile.type;
      }

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || '';
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

      const res = await fetch('/api/community', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: cleanContent || '',
          media_url: uploadedUrl || undefined,
          media_type: uploadedType || undefined,
          embed_url: embed_url || undefined
        }),
      });

      if (!res.ok) throw new Error(`Post failed: ${res.status}`);
      // Trigger a page refresh to show new post (parent will re-fetch)
      window.location.reload();
    } catch (err) {
      alert('Failed to create post: ' + err.message);
      throw err;
    }
  };

  // Handle share
  const handleShare = async (post) => {
    try {
      await addShare(post.id, userId);
      const count = await fetchShareCount(post.id);
      setShareCounts(prev => ({ ...prev, [post.id]: count }));
      const shareData = {
        title: 'Check out this post!',
        text: post.content,
        url: window.location.href
      };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        navigator.clipboard.writeText(`${post.content}\n${window.location.href}`);
        alert('Post link copied to clipboard!');
      }
    } catch (err) {
      alert('Failed to share: ' + err.message);
    }
  };

  return (
    <div className="profile-section social-card">
      <h2>Posts</h2>
      
      {/* LinguaSnaps section - shows active snaps at top of Posts tab */}
      <ProfileLinguaSnaps profileId={profile?.id} isOwnProfile={isOwnProfile} profileUser={profile} />
      
      {isOwnProfile && (
        <DockedComposer
          placeholder="What's on your mind?"
          onSubmit={handleDockedSubmit}
          isSubmitting={isSubmittingPost}
          title="Create Post"
        />
      )}
      {isLoading && (!posts || posts.length === 0) ? (
        <div className="posts-skeleton">
          <div className="post-card" style={{ opacity: 0.6, minHeight: 120 }} />
          <div className="post-card" style={{ opacity: 0.6, minHeight: 120, marginTop: 8 }} />
        </div>
      ) : posts && posts.length > 0 ? (
        <div className="posts-container social-posts-list">
          {posts.map((post) =>
            editingPostId === post.id ? (
              <div key={post.id} className="post-card editing social-post-card">
                <textarea
                  value={editedPostContent}
                  onChange={(e) => setEditedPostContent(e.target.value)}
                  className="post-textarea"
                />
                <div className="edit-actions">
                  <button className="edit-btn" onClick={() => handleSaveEditPost(post.id)}>
                    <FaSave /> Save
                  </button>
                  <button className="edit-btn cancel" onClick={handleCancelEditPost}>
                    <FaTimes /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={post.id} className="post-card social-post-card">
                {post.source === 'group' && post.group && (
                  <div className="feed-context-banner social-context-banner" style={{ backgroundImage: post.group.group_banner_url ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${post.group.group_banner_url})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="banner-context-top social-context-pills">
                      <span className="banner-pill social-context-pill">Group</span>
                      <a href={`/app/groups/${post.group.id}`} target="_blank" rel="noopener noreferrer" className="banner-name-link">
                        <span className="banner-name">{post.group.name || 'Group'}</span>
                      </a>
                    </div>
                  </div>
                )}
                {post.source === 'event' && post.event && (
                  <div className="feed-context-banner social-context-banner" style={{ backgroundImage: post.event.event_banner_url ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${post.event.event_banner_url})` : 'linear-gradient(135deg, #1e90ff 0%, #ff7f50 100%)' }}>
                    <div className="banner-context-top social-context-pills">
                      <span className="banner-pill social-context-pill">Event</span>
                      <a href={`/app/events/${post.event.id}`} target="_blank" rel="noopener noreferrer" className="banner-name-link">
                        <span className="banner-name">{post.event.title || 'Event'}</span>
                      </a>
                    </div>
                  </div>
                )}
                <div className="post-header social-post-header">
                  <div className="post-avatar">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="avatar" className="social-avatar" />
                    ) : (
                      <div className="avatar-mini-placeholder social-avatar">
                        {(profile?.display_name || profile?.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="post-info social-author-meta">
                    <h3 className="social-author-name">{profile?.display_name || profile?.name || 'User'}</h3>
                    <p className="post-date social-post-date">{new Date(post.created_at).toLocaleString()}</p>
                  </div>
                  {isOwnProfile && (
                    <div style={{ marginLeft: 'auto', position: 'relative' }}>
                      <button
                        type="button"
                        className="social-kebab-btn"
                        aria-label="Post options"
                        onClick={() =>
                          setOwnerMenuOpen(prev => ({
                            ...prev,
                            [post.id]: !prev[post.id]
                          }))
                        }
                      >
                        ⋮
                      </button>
                      {ownerMenuOpen[post.id] && (
                        <div className="social-kebab-menu">
                          <button
                            type="button"
                            className="social-kebab-item"
                            onClick={() => {
                              setEditingPostId(post.id);
                              setEditedPostContent(post.content);
                              setOwnerMenuOpen(prev => ({ ...prev, [post.id]: false }));
                            }}
                          >
                            <FaEdit />
                            Edit post
                          </button>
                          <div className="social-kebab-danger-label">Danger zone</div>
                          <button
                            type="button"
                            className="social-kebab-item danger"
                            onClick={() => {
                              setOwnerMenuOpen(prev => ({ ...prev, [post.id]: false }));
                              handleDeletePost(post.id);
                            }}
                          >
                            <FaTrash />
                            Delete post
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="post-content social-post-content">{post.content}</div>
                {post.media_url && (
                  <div className="post-media social-post-media">
                    {post.media_type === 'image' ? (
                      <img src={post.media_url} alt="Post media" />
                    ) : post.media_type === 'video' ? (
                      <video src={post.media_url} controls />
                    ) : null}
                  </div>
                )}
                {post.embed_url && (() => {
                  const platform = getEmbedPlatform(post.embed_url);
                  const menuKey = `embed-${post.id}`;
                  return (
                    <div className="post-media social-post-media" style={{ position: 'relative' }}>
                      <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                        <iframe
                          src={getEmbedSrc(post.embed_url)}
                          title="Embed"
                          style={{
                            position: 'absolute',
                            top: 0, left: 0, width: '100%', height: '100%',
                            border: 0
                          }}
                          allowFullScreen
                        />
                      </div>
                      {platform !== 'YouTube' && (
                        <div style={{ position: 'absolute', top: 8, right: 8 }} ref={embedMenuOpen[menuKey] ? embedMenuRef : null}>
                          <button
                            className="icon-btn"
                            onClick={() => toggleEmbedMenu(menuKey)}
                            title="Options"
                            style={{ background: 'rgba(0,0,0,0.6)', padding: '8px' }}
                          >
                            ⋮
                          </button>
                          {embedMenuOpen[menuKey] && (
                            <div className="embed-menu-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'rgba(20,24,36,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', minWidth: 180, zIndex: 1000 }}>
                              <button
                                onClick={() => copyEmbedLink(post.embed_url)}
                                style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <FaLink /> Copy Link
                              </button>
                              <button
                                onClick={() => viewOnPlatform(post.embed_url)}
                                style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <FaExternalLinkAlt /> View on {platform}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="social-post-actions" style={{ marginTop: 8 }}>
                  <PostReactions
                    postId={post.id}
                    stats={{
                      reactionsByType: reactionsMap[post.id] || {},
                      reactionsCount: Object.values(reactionsMap[post.id] || {}).reduce((a, b) => a + b, 0),
                      userReactionType: post.userReactionType || null,
                    }}
                    getAuthHeaders={getAuthHeaders}
                    onUpdate={(prev, next) => handleReactionUpdate(post.id, prev, next)}
                  />
                  {/* Repost */}
                  <button type="button" title="Repost" onClick={() => handleRepost(post)} className="icon-btn">
                    <FaRetweet />
                    <span className="count-badge">{repostCounts[post.id] || 0}</span>
                  </button>
                  {/* Comment */}
                  <button
                    type="button"
                    title="Comments"
                    onClick={() => {
                      setActiveCommentPostId(post.id);
                      setCommentModalOpen(true);
                    }}
                    className="icon-btn"
                  >
                    <FaRegCommentDots />
                    <span className="count-badge">{commentCounts[post.id] || 0}</span>
                  </button>
                  {/* Share */}
                  <button type="button" title="Share" onClick={() => handleShare(post)} className="icon-btn">
                    <FaShareAlt />
                    <span className="count-badge">{shareCounts[post.id] || 0}</span>
                  </button>
                </div>
                
                {/* Inline comments removed; use modal below for threaded comments */}
              </div>
            )
          )}
        </div>
      ) : (
        <p className="no-posts-msg">No posts yet.</p>
      )}
      {/* Comment Modal */}
      <Modal
        isOpen={commentModalOpen}
        onRequestClose={() => { setCommentModalOpen(false); setReplyToCommentId(null); }}
        ariaHideApp={false}
        className="forum-comment-modal"
        overlayClassName="forum-comment-modal-overlay"
      >
        <button
          onClick={() => { setCommentModalOpen(false); setReplyToCommentId(null); }}
          className="close-modal-x-btn"
          aria-label="Close modal"
        >
          &#10005;
        </button>
        {activeCommentPostId && (
          <Comments
            postId={activeCommentPostId}
            user={authUser}
            showOnlyInput={false}
            replyTo={replyToCommentId}
            onCommentAdded={() => {
              setReplyToCommentId(null);
              setCommentCounts(prev => ({
                ...prev,
                [activeCommentPostId]: (prev[activeCommentPostId] || 0) + 1,
              }));
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default PostsTab;